import { Stack, Typography, IconButton } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import colorConfigs from "../../../configs/colorConfigs";
import { useState, useRef, useEffect } from "react";
import { ICollection } from "../../../models/ICollection";
import { typo } from "../../../assets/fonts/font";
import CustomSVG from "../../../helpers/ButtomSVG/CustomSVG";

interface IProductCategoriesBarProps {
  collections: ICollection[];
  onSelectCollection: (collection: ICollection) => void;
}

const ProductCategoriesBar = ({
  collections,
  onSelectCollection,
}: IProductCategoriesBarProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const updateArrows = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scroll = (direction: "left" | "right") => {
    if (containerRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      containerRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleCollectionClick = (collection: ICollection, index: number) => {
    setCurrentIndex(index);
    onSelectCollection(collection);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", updateArrows);
      updateArrows(); // Initial check
      return () => container.removeEventListener("scroll", updateArrows);
    }
  }, []);

  useEffect(() => {
    updateArrows(); // Update arrows when collections change
  }, [collections]);

  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: colorConfigs.primary,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {showLeftArrow && (
        <IconButton
          onClick={() => scroll("left")}
          sx={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            color: colorConfigs.secondary,
          }}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
      )}

      <Stack
        ref={containerRef}
        direction="row"
        spacing={2}
        sx={{
          width: "90%", 
          height: "100%",
          margin: "0 auto",
          justifyContent: "center",
          alignItems: "center",
          overflowX: "auto",
          scrollBehavior: "smooth",
          touchAction: "pan-y", 
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {collections.map((collection, index) => (
          <Stack
            key={index}
            sx={{
              flexShrink: 0,
              justifyContent: "center",
              alignItems: "center",
              padding: "0.5rem",
              cursor: "pointer",
              position: "relative",
              touchAction: "pan-y",
            }}
            onClick={() => handleCollectionClick(collection, index)}
          >
            <Stack
              sx={{
                width: "fit-content",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                padding: "0.5rem 1rem",
              }}
            >
              <Typography sx={{ ...typo.title, color: colorConfigs.productCarouselText }}>
                {collection.name}
              </Typography>
              {index === currentIndex && (
                <Stack
                  sx={{
                    position: "absolute",
                    width: "100%",
                    justifyContent: "center",
                    alignItems: "center",
                    transform: "translateY(-50%)",
                  }}
                >
                  <CustomSVG
                    strokeColor={colorConfigs.mainbody}
                    strokeWidth={1}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
        ))}
      </Stack>

      {showRightArrow && (
        <IconButton
          onClick={() => scroll("right")}
          sx={{
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            color: colorConfigs.mainbody,
          }}
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      )}
    </Stack>
  );
};

export default ProductCategoriesBar;